import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["minRows", "maxRows"];
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
/**
 * Реализация основана на TextareaAutosize из Material UI
 * https://github.com/mui-org/material-ui/blob/21dba852b1a48cf427326884815d82b8eeca795c/packages/mui-base/src/TextareaAutosize/TextareaAutosize.js
 */
import * as React from 'react';
import { useForkRef, useIsomorphicLayoutEffect } from '@cian/react-utils';
import cx from 'classnames';
import { ownerWindow, debounce } from '../../../../internal/utils';
import styles from './styles.css';
var MAX_RERENDERS = 20;

/**
 * Компонент Textarea который подгоняет высоту под количество введенного текста
 */
export var TextareaAutosize = /*#__PURE__*/React.forwardRef(function Textarea(props, ref) {
  var minRows = props.minRows,
    maxRows = props.maxRows,
    nativeProps = _objectWithoutProperties(props, _excluded);
  var value = props.value,
    className = props.className;
  var _React$useRef = React.useRef(value != null),
    isControlled = _React$useRef.current;
  var shadowRef = React.useRef(null);
  var inputRef = React.useRef(null);
  var handleRef = useForkRef(inputRef, ref);
  var renders = React.useRef(0);
  var _React$useState = React.useState({}),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    state = _React$useState2[0],
    setState = _React$useState2[1];
  var syncHeight = React.useCallback(function () {
    var input = inputRef.current;
    var shadowInput = shadowRef.current;
    if (!input || !shadowInput) {
      return;
    }
    var containerWindow = ownerWindow(input);
    var computedStyle = containerWindow.getComputedStyle(input);

    /**
     * If input's width is shrunk and it's not visible, don't sync height.
     *
     * https://github.com/mui-org/material-ui/pull/23273
     */
    if (computedStyle.width === '0px') {
      return;
    }
    shadowInput.style.width = computedStyle.width;
    shadowInput.value = input.value || props.placeholder || 'x';
    if (shadowInput.value.slice(-1) === '\n') {
      /**
       * Certain fonts which overflow the line height will cause the textarea
       * to report a different scrollHeight depending on whether the last line
       * is empty. Make it non-empty to avoid this issue.
       *
       * https://github.com/mui-org/material-ui/pull/20575
       */
      shadowInput.value += ' ';
    }
    var boxSizing = computedStyle.boxSizing;
    var padding = getStyleIntValue(computedStyle, 'paddingBottom') + getStyleIntValue(computedStyle, 'paddingTop');
    var border = getStyleIntValue(computedStyle, 'borderBottomWidth') + getStyleIntValue(computedStyle, 'borderTopWidth');

    // The height of the inner content
    var innerHeight = shadowInput.scrollHeight;

    // Measure height of a textarea with a single row
    shadowInput.value = 'x';
    var singleRowHeight = shadowInput.scrollHeight;

    // The height of the outer content
    var outerHeight = innerHeight;
    if (minRows) {
      outerHeight = Math.max(Number(minRows) * singleRowHeight, outerHeight);
    }
    if (maxRows) {
      outerHeight = Math.min(Number(maxRows) * singleRowHeight, outerHeight);
    }
    outerHeight = Math.max(outerHeight, singleRowHeight);

    // Take the box sizing into account for applying this value as a style.
    var outerHeightStyle = outerHeight + (boxSizing === 'border-box' ? padding + border : 0);
    // Need a large enough different to allow scrolling.
    // This prevents infinite rendering loop.
    var overflow = Math.abs(outerHeight - innerHeight) <= 1;
    setState(function (prevState) {
      /**
       * Need a large enough difference to update the height.
       * This prevents infinite rendering loop.
       *
       * https://github.com/mui-org/material-ui/issues/17672
       */
      if (renders.current < MAX_RERENDERS && (
      // Has any changes
      outerHeightStyle > 0 && Math.abs((prevState.outerHeightStyle || 0) - outerHeightStyle) > 1 || prevState.overflow !== overflow)) {
        renders.current += 1;
        return {
          overflow: overflow,
          outerHeightStyle: outerHeightStyle
        };
      }
      if (process.env.NODE_ENV !== 'production') {
        if (renders.current >= MAX_RERENDERS) {
          console.error(['UI-KIT: Too many re-renders. The layout is unstable.', 'TextareaAutosize limits the number of renders to prevent an infinite loop.'].join('\n'));
        }
      }
      return prevState;
    });
  }, [maxRows, minRows, props.placeholder]);
  React.useEffect(function () {
    var handleResize = debounce(function () {
      renders.current = 0;
      syncHeight();
    });
    var containerWindow = ownerWindow(inputRef.current);
    containerWindow.addEventListener('resize', handleResize);
    var resizeObserver;

    // Нужен для синхронизации размеров, когда изменяется ширина
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(inputRef.current);
    }
    return function () {
      handleResize.cancel();
      containerWindow.removeEventListener('resize', handleResize);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [syncHeight]);
  useIsomorphicLayoutEffect(function () {
    syncHeight();
  });
  React.useEffect(function () {
    renders.current = 0;
  }, [value]);
  var handleChange = function handleChange(e) {
    renders.current = 0;
    if (!isControlled) {
      syncHeight();
    }
    if (props.onChange) {
      props.onChange(e);
    }
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("textarea", _extends({}, nativeProps, {
    ref: handleRef,
    onChange: handleChange,
    rows: minRows,
    style: _objectSpread({
      height: state.outerHeightStyle,
      overflow: state.overflow ? 'hidden' : undefined
    }, props.style)
  })), /*#__PURE__*/React.createElement("textarea", {
    "aria-hidden": true,
    ref: shadowRef,
    readOnly: true,
    tabIndex: -1,
    className: cx(className, styles['shadow']),
    style: props.style
  }));
});
function getStyleIntValue(computedStyle, property) {
  return parseInt(computedStyle[property], 10) || 0;
}